/** Shared MFA resend / login email-delivery hint copy and cooldown math. */

export type MfaResendFeedback =
  | { kind: 'sent'; message: string; cooldownSeconds: number }
  | { kind: 'send_failed'; message: string }
  | { kind: 'cooldown'; seconds: number };

/**
 * Wall-clock remaining seconds for a cooldown that started at `startedAtMs`
 * with an initial duration of `startSeconds`. Prefer this over decrementing a
 * counter each tick so background tab throttling cannot drift the timer.
 */
export function remainingCooldownSeconds(
  startSeconds: number,
  startedAtMs: number,
  nowMs: number
): number {
  if (!Number.isFinite(startSeconds) || startSeconds <= 0) return 0;
  if (!Number.isFinite(startedAtMs) || !Number.isFinite(nowMs)) return 0;
  if (nowMs < startedAtMs) return Math.max(0, Math.floor(startSeconds));
  const elapsed = Math.floor((nowMs - startedAtMs) / 1000);
  return Math.max(0, Math.floor(startSeconds) - elapsed);
}

/** Parse resend API result into UI state (cooldown is numeric for a live timer). */
export function parseMfaResendResult(result: {
  sent: boolean;
  cooldown_seconds?: number;
  error?: string;
}): MfaResendFeedback {
  if (result.sent) {
    const cooldownSeconds = Math.max(0, Math.floor(result.cooldown_seconds ?? 0));
    return {
      kind: 'sent',
      message: 'A new code was sent to your email. Check your inbox and spam folder.',
      cooldownSeconds,
    };
  }
  const cooldown = Math.max(0, Math.floor(result.cooldown_seconds ?? 0));
  if (result.error === 'send_failed' || cooldown <= 0) {
    return {
      kind: 'send_failed',
      message: 'We could not send the email. Try Resend code again in a moment.',
    };
  }
  return { kind: 'cooldown', seconds: cooldown };
}

export function mfaLoginEmailHint(mfaEmailSent: boolean | undefined): string | null {
  if (mfaEmailSent === false) {
    return 'We could not send the verification email. Use Resend code to try again.';
  }
  return null;
}

export function formatMfaResendCooldown(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return s === 1
    ? 'Please wait 1s before requesting another code.'
    : `Please wait ${s}s before requesting another code.`;
}

/** Wire parseMfaResendResult into Login / Register / dialog state setters. */
export function applyMfaResendFeedback(
  parsed: MfaResendFeedback,
  setters: {
    setHint: (message: string | null) => void;
    setCooldownSeconds: (seconds: number | null) => void;
    bumpCooldownToken: () => void;
  }
): void {
  if (parsed.kind === 'sent') {
    setters.setHint(parsed.message);
    if (parsed.cooldownSeconds > 0) {
      setters.setCooldownSeconds(parsed.cooldownSeconds);
      setters.bumpCooldownToken();
    } else {
      setters.setCooldownSeconds(null);
    }
    return;
  }
  if (parsed.kind === 'cooldown') {
    setters.setHint(null);
    setters.setCooldownSeconds(parsed.seconds);
    setters.bumpCooldownToken();
    return;
  }
  setters.setCooldownSeconds(null);
  setters.setHint(parsed.message);
}
